import { TOOL_IDS } from "@tribunalea/shared";

export const INSTRUMENTA_TOOLS_RECENT_STORAGE_KEY =
  "tribunalea:instrumenta-tools-recent";

const RECENT_TOOLS_COOKIE_KEY = "tribunalea_instrumenta_tools_recent";
const RECENT_TOOLS_LIMIT = 3;
const RECENT_TOOLS_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

export const INSTRUMENTA_TOOLS = [
  {
    id: "normas",
    accessId: TOOL_IDS.INSTRUMENTA_NORMS,
    label: "Normas",
    description: "Consultá legislación y textos vigentes.",
    path: "/normas",
  },
  {
    id: "jurisprudencia",
    accessId: TOOL_IDS.INSTRUMENTA_JURISPRUDENCE,
    label: "Jurisprudencia",
    description: "Buscá fallos por texto y criterios.",
    path: "/jurisprudencia",
  },
  {
    id: "doctrina",
    accessId: TOOL_IDS.INSTRUMENTA_DOCTRINE,
    label: "Doctrina",
    description: "Explorá obras y fuentes doctrinarias.",
    path: "/doctrina",
  },
  {
    id: "biblioteca",
    accessId: TOOL_IDS.INSTRUMENTA_LIBRARY,
    label: "Biblioteca IA",
    description: "Trabajá con tus fuentes junto a IA.",
    path: "/biblioteca",
  },
  {
    id: "procesador-documental",
    accessId: TOOL_IDS.INSTRUMENTA_DOCUMENT_PROCESSOR,
    label: "Procesador documental",
    description: "Procesá documentos y extraé información.",
    path: "/procesador-documental",
  },
  {
    id: "calculadora-intereses",
    accessId: TOOL_IDS.INSTRUMENTA_JUDICIAL_INTEREST,
    label: "Calculadora de Intereses",
    description: "Calculá intereses judiciales.",
    path: "/calculadora-intereses",
  },
  {
    id: "consulta-fiscal",
    accessId: TOOL_IDS.INSTRUMENTA_TAX_ID,
    label: "Buscador de CUIT",
    description: "Consultá información fiscal.",
    path: "/consulta-fiscal",
  },
  {
    id: "matriculas-profesionales",
    accessId: TOOL_IDS.INSTRUMENTA_PROFESSIONALS,
    label: "Matrículas",
    description: "Buscá matrículas profesionales.",
    path: "/matriculas-profesionales",
  },
  {
    id: "buscador-sentencias",
    accessId: TOOL_IDS.INSTRUMENTA_SENTENCES,
    label: "Sentencias",
    description: "Analizá sentencias y sus conceptos.",
    path: "/buscador-sentencias",
  },
];

const instrumentaToolIds = new Set(INSTRUMENTA_TOOLS.map((tool) => tool.id));

export function normalizeRecentInstrumentaTools(
  entries,
  limit = RECENT_TOOLS_LIMIT,
) {
  if (!Array.isArray(entries)) return [];

  const unique = new Map();
  entries.forEach((entry) => {
    if (
      !entry
      || !instrumentaToolIds.has(entry.id)
      || !Number.isFinite(entry.usedAt)
    ) return;
    const current = unique.get(entry.id);
    if (!current || current.usedAt < entry.usedAt) {
      unique.set(entry.id, { id: entry.id, usedAt: entry.usedAt });
    }
  });

  return [...unique.values()]
    .sort((left, right) => right.usedAt - left.usedAt)
    .slice(0, limit);
}

export function markInstrumentaToolUsed(entries, toolId, usedAt = Date.now()) {
  if (!instrumentaToolIds.has(toolId)) {
    return normalizeRecentInstrumentaTools(entries);
  }
  return normalizeRecentInstrumentaTools([
    { id: toolId, usedAt },
    ...(Array.isArray(entries) ? entries : []),
  ]);
}

function parseEntries(value) {
  if (!value) return null;
  try {
    return normalizeRecentInstrumentaTools(JSON.parse(value));
  } catch {
    return null;
  }
}

function readCookie(cookie) {
  const prefix = `${RECENT_TOOLS_COOKIE_KEY}=`;
  const entry = String(cookie || "")
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));
  if (!entry) return null;
  try {
    return decodeURIComponent(entry.slice(prefix.length));
  } catch {
    return null;
  }
}

export function readRecentInstrumentaTools({
  cookie = typeof document !== "undefined" ? document.cookie : "",
  storage = typeof window !== "undefined" ? window.localStorage : null,
} = {}) {
  const cookieEntries = parseEntries(readCookie(cookie));
  if (cookieEntries) return cookieEntries;

  try {
    return parseEntries(storage?.getItem(INSTRUMENTA_TOOLS_RECENT_STORAGE_KEY))
      || [];
  } catch {
    return [];
  }
}

export function persistRecentInstrumentaTool(toolId, {
  cookie = typeof document !== "undefined" ? document.cookie : "",
  documentObject = typeof document !== "undefined" ? document : null,
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
  protocol = typeof window !== "undefined" ? window.location.protocol : "",
  storage = typeof window !== "undefined" ? window.localStorage : null,
  usedAt = Date.now(),
} = {}) {
  const next = markInstrumentaToolUsed(
    readRecentInstrumentaTools({ cookie, storage }),
    toolId,
    usedAt,
  );
  const serialized = JSON.stringify(next);

  try {
    storage?.setItem(INSTRUMENTA_TOOLS_RECENT_STORAGE_KEY, serialized);
  } catch {
    // The shared cookie remains available when local storage is blocked.
  }

  if (documentObject) {
    const sharedDomain = hostname === "tribunalea.com"
      || hostname.endsWith(".tribunalea.com")
      ? "; Domain=.tribunalea.com"
      : "";
    const secure = protocol === "https:" ? "; Secure" : "";
    documentObject.cookie = `${RECENT_TOOLS_COOKIE_KEY}=${encodeURIComponent(
      serialized,
    )}; Path=/; Max-Age=${RECENT_TOOLS_COOKIE_MAX_AGE_SECONDS}`
      + `; SameSite=Lax${secure}${sharedDomain}`;
  }

  return next;
}

export function buildInstrumentaToolUrl(instrumentaUrl, path) {
  return new URL(
    path,
    `${instrumentaUrl.replace(/\/+$/, "")}/`,
  ).toString();
}
